/*
 * THE SCORE, READ SO IT ADDS UP: "33 · medium", and a breakdown whose rows sum to it.
 *
 * The old card worded the score as a verdict ("33/100 · below the threshold of
 * 40") with its reasons in a hover whose chips added to 22. Since PR #153 the
 * server sends parts that sum to the score exactly (src/automations/scoreParts.js:
 * chips[].points + floor_points === score), and since PR #154 the band, the bands,
 * who was scored and the arrival score. This module only words them:
 *
 *   - the badge: "80 · hot", its four band segments (the Scoring page's grey ramp,
 *     never yellow, red or green), or one segment when the badge is small;
 *   - the breakdown rows: the group, what the scorer read and the row it matched
 *     ("computer software → B2B SaaS"), the points ("+14", "−4", "0 of 24");
 *     unknown facts read "unknown · Add" because the score is live and filling
 *     one in changes it;
 *   - the floor row when penalties pushed the raw score under 0, or the rows
 *     would not add up;
 *   - the bar's three parts from one sentence: "Lost 30 to missing data, 37 to
 *     fit." Missing data is the sum of the ceilings of the groups we had no fact
 *     for; fit is the rest of the hundred;
 *   - the reason lines ("Why 80: …", "Fits on industry and size, funding unknown.")
 *     and the edge states (blocked, scored on a colleague, scored on arrival).
 *
 * Null in, { show: false } out: with no after-hours config there is no score, and
 * the record shows no badge at all, never a dash.
 */
import { enumWords, moneyShort, shortDate, listWords } from "./format.mjs";

export const DEFAULT_BANDS = [25, 40, 55];
export const BAND_NAMES = ["low", "medium", "high", "hot"];
// The Scoring page's ramp (brief: "low #C9C6BB, medium #8F8C83, high #3A3933, hot
// black"). record.css carries the same four as --rec-band-*; the model names the
// band and the stylesheet paints it.
export const BAND_COLORS = { low: "#C9C6BB", medium: "#8F8C83", high: "#3A3933", hot: "#0A0A0A" };

/** src/scoring.js bandOf, for a payload that predates `band`. */
export function bandOf(total, bands = DEFAULT_BANDS, blocked = false) {
  if (blocked) return "low";
  const [medium, high, hot] = Array.isArray(bands) && bands.length === 3 ? bands : DEFAULT_BANDS;
  if (total >= hot) return "hot";
  if (total >= high) return "high";
  if (total >= medium) return "medium";
  return "low";
}

const GROUP_NAMES = {
  industry: "Industry",
  headcount: "Headcount",
  title: "Title",
  funding: "Funding",
  age: "Company age",
  country: "Country",
  keywords: "Keywords",
  mailbox: "Mailbox",
};

// The plain word for a group in "Fits on industry and size".
const GROUP_WORDS = {
  industry: "industry",
  headcount: "size",
  title: "title",
  funding: "funding",
  age: "age",
  country: "location",
  keywords: "keywords",
  mailbox: "mailbox",
};

// Where "Add" writes, for an unknown fact. Columns go through PATCH /companies/:id
// or /contacts/:id; the property keys through properties_set (PR #154's allowlist).
const ADD_TARGET = {
  industry: { target: "company", field: "industry", label: "Add" },
  headcount: { target: "company", field: "employee_range", label: "Add" },
  title: { target: "contact", field: "job_title", label: "Add" },
  funding: { target: "company", prop: "total_funding", label: "Add" },
  age: { target: "company", prop: "founded_year", label: "Add" },
  country: { target: "company", field: "country", label: "Add" },
};

const MINUS = "−";
const signed = (n) => (n > 0 ? `+${n}` : n < 0 ? `${MINUS}${Math.abs(n)}` : "0");

const SHORT_COUNTRY = { "united states": "the US", "united kingdom": "the UK", "united arab emirates": "the UAE", netherlands: "the Netherlands" };

const same = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

/** What the scorer read for a group, and the row it matched, as one line. */
function readLine(chip, ls) {
  const matched = (chip.matched || [])[0] || null;
  const arrow = (fact) => (matched && !same(fact, matched) ? `${fact} → ${matched}` : String(fact));
  if (chip.group === "keywords") {
    if (chip.unknown) return "nothing to read";
    return chip.matched && chip.matched.length ? chip.matched.join(", ") : "none matched";
  }
  if (chip.unknown) return chip.group === "age" ? "Founded · unknown" : "unknown";
  switch (chip.group) {
    case "industry":
      return arrow(enumWords(chip.fact));
    case "headcount":
      return `${chip.fact} employees`;
    case "funding": {
      const amount = ls && Number.isFinite(Number(ls.funding_amount)) && ls.funding_amount !== null ? moneyShort(ls.funding_amount) : null;
      const stage = matched || chip.fact;
      return amount ? `${amount} → ${stage}` : String(stage);
    }
    case "age":
      return `${chip.fact} years`;
    case "mailbox":
      if (matched && /free/i.test(matched)) return "free mailbox";
      return "company mailbox";
    default:
      return arrow(chip.fact);
  }
}

/** One row of the breakdown: { group, name, read, points, ceiling, display, unknown, add }. */
export function breakdownRow(chip, ls) {
  const points = Number(chip.points) || 0;
  return {
    group: chip.group,
    name: GROUP_NAMES[chip.group] || chip.group,
    read: readLine(chip, ls),
    points,
    ceiling: Number(chip.ceiling) || 0,
    // "0 of 24" says what a zero could have been; a non-zero part is signed.
    display: points === 0 ? `0 of ${Number(chip.ceiling) || 0}` : signed(points),
    unknown: !!chip.unknown,
    add: chip.unknown ? ADD_TARGET[chip.group] || null : null,
    strong: points !== 0,
  };
}

/** "Why 80: B2B SaaS, 180 people, VP Marketing, Series B+ in the US." (the parts). */
export function reasonParts(ls) {
  const chips = (ls && ls.chips) || [];
  const strong = chips.filter((c) => !c.unknown && Number(c.points) >= 5 && c.group !== "mailbox");
  const parts = [];
  let where = null;
  for (const c of strong) {
    const matched = (c.matched || [])[0];
    switch (c.group) {
      case "industry":
        parts.push(matched || enumWords(c.fact));
        break;
      case "headcount":
        parts.push(`${c.fact} people`);
        break;
      case "title":
        parts.push(String(c.fact));
        break;
      case "funding":
        parts.push(matched || String(c.fact));
        break;
      case "age":
        parts.push(`${c.fact} years old`);
        break;
      case "keywords":
        if (c.matched && c.matched.length) parts.push(c.matched.join(", "));
        break;
      case "country":
        where = SHORT_COUNTRY[String(c.fact).toLowerCase()] || String(c.fact);
        break;
      default:
        break;
    }
  }
  if (!parts.length && !where) return "";
  const head = parts.join(", ");
  return where ? `${head}${head ? " " : ""}in ${where}` : head;
}

/** "Fits on industry and size, funding unknown." */
export function fitsOnLine(ls) {
  const chips = (ls && ls.chips) || [];
  const on = chips.filter((c) => !c.unknown && Number(c.points) >= 5 && c.group !== "mailbox").map((c) => GROUP_WORDS[c.group]);
  const unknown = chips.filter((c) => c.unknown && Number(c.ceiling) > 0 && c.group !== "keywords").map((c) => GROUP_WORDS[c.group]);
  const head = on.length ? `Fits on ${listWords(on)}` : "Fits on nothing we know yet";
  return `${head}${unknown.length ? `, ${listWords(unknown)} unknown` : ""}.`;
}

/** `Blocked by “job” in the title`, from the scorer's own sentence. */
export function blockedLine(reason) {
  const s = String(reason || "");
  const m = s.match(/Negative keyword "(.+?)" matched in (.+?):/);
  if (m) return `Blocked by “${m[1]}” in ${m[2]}`;
  return s ? `Blocked: ${s}` : "Blocked";
}

/**
 * Everything the badge and the breakdown render.
 *
 *   show        false when there is no score: render nothing
 *   blocked     the hard block: "0 · blocked" and the phrase that blocked it
 *   score, band, bands, label ("33 · medium")
 *   segments    [{ band, filled }] ×4, the featured badge's ramp
 *   rows        breakdown rows, in the scorer's group order
 *   floor       { name, read, points, display } when floor_points ≠ 0, else null
 *   total       Σ rows + floor, which equals score
 *   missing, fit, sentence ("Lost 30 to missing data, 37 to fit.")
 *   reason      "B2B SaaS, 180 people, VP Marketing, Series B+ in the US"
 *   fitsOn      "Fits on industry and size, funding unknown."
 *   scoredOn    "Scored on Shahar Bahat (point of contact)" on a colleague's record
 *   arrival     "Scored 52 on arrival, Sep 12", only when it differs from today
 *   bandsLine, thresholdLine
 */
export function scoreView(ls, { contactId = null, pocId = null, now = new Date() } = {}) {
  if (!ls || ls.score === null || ls.score === undefined) return { show: false };
  const bands = Array.isArray(ls.bands) && ls.bands.length === 3 ? ls.bands : DEFAULT_BANDS;
  const blocked = !!ls.blocked_reason;
  const score = blocked ? 0 : Number(ls.score) || 0;
  const band = ls.band || bandOf(score, bands, blocked);
  const level = BAND_NAMES.indexOf(band);
  const segments = BAND_NAMES.map((b, i) => ({ band: b, filled: !blocked && i <= level }));

  const scoredOn =
    ls.contact_id && contactId && String(ls.contact_id) !== String(contactId) && ls.contact_name
      ? `Scored on ${ls.contact_name}${pocId && String(pocId) === String(ls.contact_id) ? " (point of contact)" : ""}`
      : null;
  const arrival =
    ls.arrival && ls.arrival.score !== undefined && Number(ls.arrival.score) !== score
      ? `Scored ${ls.arrival.score} on arrival${ls.arrival.at ? `, ${shortDate(ls.arrival.at, { now })}` : ""}`
      : null;
  const [medium, high, hot] = bands;
  const bandsLine = `Bands: low under ${medium} · medium from ${medium} · high from ${high} · hot from ${hot}.`;
  const thresholdLine =
    ls.threshold !== null && ls.threshold !== undefined
      ? `The after-hours auto-reply answers at ${ls.threshold} and up.`
      : null;

  if (blocked) {
    return {
      show: true,
      blocked: true,
      score: 0,
      band: "low",
      bands,
      label: "0 · blocked",
      segments,
      rows: [],
      floor: null,
      total: 0,
      missing: 0,
      fit: 0,
      sentence: "",
      reason: "",
      fitsOn: "",
      blockedLine: blockedLine(ls.blocked_reason),
      scoredOn,
      arrival,
      bandsLine,
      thresholdLine,
    };
  }

  const rows = (ls.chips || []).map((c) => breakdownRow(c, ls));
  const floorPoints = Number(ls.floor_points) || 0;
  const floor = floorPoints
    ? { name: "Floor", read: "penalties took it under 0, and the score stops at 0", points: floorPoints, display: signed(floorPoints) }
    : null;
  const total = rows.reduce((s, r) => s + r.points, 0) + floorPoints;
  const missing = (ls.chips || []).filter((c) => c.unknown).reduce((s, c) => s + (Number(c.ceiling) || 0), 0);
  const fit = Math.max(0, 100 - score - missing);
  return {
    show: true,
    blocked: false,
    score,
    band,
    bands,
    label: `${score} · ${band}`,
    segments,
    rows,
    floor,
    total,
    missing,
    fit,
    sentence: `Lost ${missing} to missing data, ${fit} to fit.`,
    bar: { score: Math.max(0, Math.min(100, score)), missing: Math.min(missing, 100 - Math.max(0, score)), fit },
    reason: reasonParts(ls),
    fitsOn: fitsOnLine(ls),
    blockedLine: null,
    scoredOn,
    arrival,
    bandsLine,
    thresholdLine,
  };
}

/** "Would score 33 today": the score where it has left the header (clients, lost). */
export function wouldScore(view) {
  if (!view || !view.show) return null;
  return view.blocked ? "Would score 0 today, blocked" : `Would score ${view.score} today`;
}
